import React, { useState } from 'react';
import { Send, Mail, User, Car, MessageSquare, CheckCircle, Sparkles, Phone } from 'lucide-react';
import { allCars, companyInfo } from '../data/mock';

const InquirySection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    car: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Create mailto link with form data
    const subject = encodeURIComponent(`Car Inquiry: ${formData.car || 'General Inquiry'}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nCar Interested In: ${formData.car}\n\nMessage:\n${formData.message}`
    );
    
    // Open mailto link
    window.location.href = `mailto:${companyInfo.email}?subject=${subject}&body=${body}`;

    // Show success state
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', car: '', message: '' });
      }, 3000);
    }, 1000);
  };

  return (
    <section id="inquiry" className="py-20 bg-black relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
      <div className="absolute top-1/3 right-0 w-80 h-80 bg-amber-500/5 rounded-full blur-[100px] animate-float" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Info */}
          <div className="animate-fadeInLeft">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
              <span className="text-amber-400 text-sm font-medium">Get In Touch</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to
              <span className="text-gold-gradient"> Drive?</span>
            </h2>
            <p className="text-gray-400 text-lg mb-8 leading-relaxed">
              Fill out the inquiry form and our team will get back to you within 24 hours.
              Let us help you find the perfect vehicle for your needs.
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-4 text-gray-400 group">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-xl flex items-center justify-center group-hover:shadow-[0_0_25px_rgba(212,160,23,0.3)] transition-all duration-300">
                  <Phone className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Call us at</p>
                  <a 
                    href={`tel:${companyInfo.phone.replace(/-/g, '')}`} 
                    className="text-white hover:text-amber-400 transition-colors font-medium text-lg"
                  >
                    {companyInfo.phone}
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-4 text-gray-400 group">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-xl flex items-center justify-center group-hover:shadow-[0_0_25px_rgba(212,160,23,0.3)] transition-all duration-300">
                  <Mail className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email us at</p>
                  <a 
                    href={`mailto:${companyInfo.email}`} 
                    className="text-white hover:text-amber-400 transition-colors font-medium"
                  >
                    {companyInfo.email}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="card-gold rounded-3xl p-8 animate-fadeInRight">
            {isSubmitted ? (
              <div className="text-center py-12 animate-scaleIn">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-glow">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Email Client Opened!</h3>
                <p className="text-gray-400">Complete sending the email in your email client.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field */}
                <div className="animate-fadeInUp animation-delay-100">
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Your Name
                  </label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-amber-400 transition-colors" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="w-full bg-black/50 border border-gray-800 focus:border-amber-500 rounded-xl py-3.5 pl-12 pr-4 text-white placeholder-gray-600 transition-all duration-300 outline-none"
                    />
                  </div>
                </div>

                {/* Email Field */}
                <div className="animate-fadeInUp animation-delay-200">
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Your Email
                  </label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-amber-400 transition-colors" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="w-full bg-black/50 border border-gray-800 focus:border-amber-500 rounded-xl py-3.5 pl-12 pr-4 text-white placeholder-gray-600 transition-all duration-300 outline-none"
                    />
                  </div>
                </div>

                {/* Car Selection */}
                <div className="animate-fadeInUp animation-delay-300">
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Car Interested In
                  </label>
                  <div className="relative group">
                    <Car className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-amber-400 transition-colors" />
                    <select
                      name="car"
                      value={formData.car}
                      onChange={handleChange}
                      required
                      className="w-full bg-black/50 border border-gray-800 focus:border-amber-500 rounded-xl py-3.5 pl-12 pr-10 text-white appearance-none cursor-pointer transition-all duration-300 outline-none"
                    >
                      <option value="" className="bg-black">Select a vehicle</option>
                      {allCars.map(car => (
                        <option key={car.id} value={car.name} className="bg-black">
                          {car.name} - ${car.price}/day
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Message Field */}
                <div className="animate-fadeInUp animation-delay-400">
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Additional Message (Optional)
                  </label>
                  <div className="relative group">
                    <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-gray-500 group-focus-within:text-amber-400 transition-colors" />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Tell us about your rental needs..."
                      className="w-full bg-black/50 border border-gray-800 focus:border-amber-500 rounded-xl py-3.5 pl-12 pr-4 text-white placeholder-gray-600 transition-all duration-300 outline-none resize-none"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 btn-gold py-4 rounded-xl text-lg font-bold disabled:opacity-70 disabled:cursor-not-allowed animate-fadeInUp animation-delay-500"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Opening Email...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Inquiry
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InquirySection;
